import { IUserToken } from '@module/authentication/models';
import { IAssignedSite, IParts, IRequestReportDetails, ISite, ISubmitRequestResult } from '@module/shared';
import moment from 'moment';

const ROOT_URL_V1 = `${environmentSettings.webapiUrl}/v1/Request/`;
const ROOT_URL_V2 = `${environmentSettings.webapiUrl}/v2/Request/`;

export async function GetRequestDetailsByRequestId(loggedInUser: IUserToken, sparePartsUploadDetailsID: string): Promise<IRequestReportDetails> {  
  const response = await FetchRequest(loggedInUser, sparePartsUploadDetailsID);
  const contentType = response.headers.get('content-type');
  if (response.ok && contentType && contentType.indexOf('application/json') !== -1) {
    const json = await response.json();
    return mapApiResponseToReportDetails(json, loggedInUser, sparePartsUploadDetailsID);
    } else {
      return Promise.reject({status: response.status, statusText: response.statusText});
    }
}

export function FetchRequest(loggedInUser: IUserToken, sparePartsUploadDetailsID: string): Promise<Response> {
  const headers = new Headers();
  headers.append('Authorization', 'Bearer ' + loggedInUser.access_token);
  headers.append('pragma', 'no-cache');
  headers.append('cache-control', 'no-cache');

  return fetch(`${ROOT_URL_V1}${sparePartsUploadDetailsID}`, { headers, cache: 'no-store' });
}

async function mapApiResponseToReportDetails(apiRequest: any, loggedInUser: IUserToken, sparePartsUploadDetailsID: string) {
  const parts = await GetParts(loggedInUser, sparePartsUploadDetailsID);
  const request: IRequestReportDetails = {
    sparePartsUploadDetailsID: apiRequest.sparePartsUploadDetailsID,
    requestID: apiRequest.requestID,
    sparePartsUploadID: apiRequest.sparePartsUploadID,
    leadGenerated: apiRequest.leadGenerated,
    contactName: apiRequest.contactName,
    contactEmail: apiRequest.contactEmail,
    companyName: apiRequest.companyName,
    phoneNumber: apiRequest.phoneNumber,
    location: apiRequest.location,
    postalCode: apiRequest.postalCode,
    country: apiRequest.country,
    condition: apiRequest.condition,
    downtime: apiRequest.downtime,
    comments: apiRequest.comments,
    storage: apiRequest.storage,
    awareness: apiRequest.awareness,
    uploaded: apiRequest.uploaded ? moment(apiRequest.uploaded).format('MM/DD/YYYY HH:mm:ss') : undefined,
    partsSpecialistName: apiRequest.partsSpecialistName,
    partsSpecialistEmail: apiRequest.partsSpecialistEmail,
    statusName: apiRequest.statusName,
    matchedParts: parts ? parts.matchedSpareParts : [],
    unMatchedParts: parts ? parts.unmatchedSpareParts : [],
    associatedSite: apiRequest.associatedSite,
    siteId: apiRequest.siteId ? apiRequest.siteId : undefined,
    hasExistingParts: (apiRequest.sitePartCount > 0),
    inventoryUploaded: apiRequest.inventoryUploaded,
    inventorySource: apiRequest.inventorySource
  };
  return request;
}

export async function GetParts(loggedInUser: IUserToken, sparePartsUploadDetailsID: string): Promise<IParts> {
  const response = await FetchParts(loggedInUser, sparePartsUploadDetailsID);
  const contentType = response.headers.get('content-type');
  
  if (response.ok && contentType && contentType.indexOf('application/json') !== -1) {
    const json = await response.json();
    return json;
    } else {
      return Promise.reject({status: response.status, statusText: response.statusText});
    }
}

export function FetchParts(loggedInUser: IUserToken, sparePartsUploadDetailsID: string): Promise<Response> {
  const headers = new Headers();
  headers.append('Authorization', 'Bearer ' + loggedInUser.access_token);
  headers.append('pragma', 'no-cache');
  headers.append('cache-control', 'no-cache');
  const paramUrl = 'sparePartsUploadDetailsID=' + sparePartsUploadDetailsID;
  return fetch(`${ROOT_URL_V2 + 'parts?' + paramUrl}`, { headers, cache: 'no-store' });
}

export async function approveRejectDetailedReportRequest(loggedInUser: IUserToken, sparePartsUploadId: string, uploadStatus: number, commentsParam: string): Promise<any> {
  const url = `${environmentSettings.webapiUrl}/v1/Request/UpdateStatus`;
  const headers = new Headers();
  headers.append('Authorization', 'Bearer ' + loggedInUser.access_token);
  headers.append('content-type', 'application/json');  
  
  const postBody = {
    requestDetailsId: sparePartsUploadId, 
    requestStatus: uploadStatus, 
    comments: commentsParam
  };

  const response = await fetch(url, {
    headers,
    method: 'PATCH',
    body: JSON.stringify(postBody)
  });

  if (response.ok) {
    return Promise.resolve(true);
  } else {
    return Promise.reject(`System error: ${response.status} ${response.statusText}`);
  }
}

export async function TakeOwnership(loggedInUser: IUserToken, sparePartsUploadDetailsID: string): Promise<any>  {
  const url = `${environmentSettings.webapiUrl}/v1/Request/TakeOwnership?id=` + sparePartsUploadDetailsID;
  const headers = new Headers(); 
  headers.append('Authorization', 'Bearer ' + loggedInUser.access_token);
  const response = await fetch(url, { headers, method: 'POST'});
  if (response.ok) {
    return Promise.resolve(true);
  } else {
    return Promise.reject(`System error: ${response.status} ${response.statusText}`);
  }
}

export async function GetSite(loggedInUser: IUserToken, servIsId: string): Promise<ISite> {
  const url = `${environmentSettings.webapiUrl}/v1/Request/GetSite?servIsId=` + servIsId;
  const headers = new Headers(); 
  headers.append('Authorization', 'Bearer ' + loggedInUser.access_token);
  headers.append('pragma', 'no-cache');
  headers.append('cache-control', 'no-cache');
  const response = await fetch(url, { headers, cache: 'no-store' });
  const contentType = response.headers.get('content-type');
  if (response.ok && contentType && contentType.indexOf('application/json') !== -1) {
    const json = await response.json();
    return json;
  } else {
    return Promise.reject({status: response.status, statusText: response.statusText});
  }
}

export async function AssignSite(loggedInUser: IUserToken, requestId: string, site?: ISite): Promise<IAssignedSite> {
  const url = `${environmentSettings.webapiUrl}/v1/Request/AssignSite?requestId=${requestId}`;
  const headers = new Headers(); 
  headers.append('Authorization', 'Bearer ' + loggedInUser.access_token);
  headers.append('pragma', 'no-cache');
  headers.append('cache-control', 'no-cache');
  headers.append('content-type', 'application/json'); 
  const response = await fetch(url, { headers, method: 'PATCH', body: site ? JSON.stringify(site) : null });
  if (response.ok) {
    const json = await response.json();
    return json;
  } else {
    return Promise.reject({status: response.status, statusText: response.statusText});
  }
}

export async function DeleteSite(loggedInUser: IUserToken, requestId: string): Promise<IAssignedSite> {
  const url = `${environmentSettings.webapiUrl}/v1/Request/DeleteSite?requestId=${requestId}`;
  const headers = new Headers(); 
  headers.append('Authorization', 'Bearer ' + loggedInUser.access_token);
  headers.append('pragma', 'no-cache');
  headers.append('cache-control', 'no-cache');
  headers.append('content-type', 'application/json'); 
  const response = await fetch(url, { headers, method: 'DELETE',  cache: 'no-store' });
  if (response.ok) {
    const json = await response.json();
    return json;
  } else {
    return Promise.reject({status: response.status, statusText: response.statusText});
  }
}

export async function SubmitToPartsManager(loggedInUser: IUserToken, requestId: string, servIsId: string): Promise<ISubmitRequestResult> {
  const url = `${ROOT_URL_V1}Submit?requestId=${requestId}&servIsId=${servIsId}`;
  const headers = new Headers(); 
  headers.append('Authorization', 'Bearer ' + loggedInUser.access_token);
  headers.append('pragma', 'no-cache');
  headers.append('cache-control', 'no-cache');
  const response = await fetch(url, { headers, method: 'POST' });
  if (response.ok) {
    const json = await response.json();
    return json;
  } else {
    throw {status: response.status, statusText: response.statusText};
  }
  
}
